import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Helpers
import { isThreeEM, isWeatherStation, real_id } from 'src/helpers/interface_helpers';
import { addDate, dateFormat } from 'src/helpers/date_helper';
import { getDeviceTimeZoneDate } from '../core/chart_helper';
import { user_settings } from 'src/stores';
import { isBleStatisticsDeviceToUTC, rangeDates } from 'src/helpers/statistics/ui/chart_control_helpers';
import { getNetMetering } from '../core/net_metering/chart_net_metering_helpers';
import { addLastHourConsumption } from 'src/helpers/statistics/core/last_hour_helpers/last_hour_consumption_helpers';
import { addLastHourMeasurement } from '../core/last_hour_helpers/last_hour_measurement_helpers';
import { parseStatisticsData } from 'src/helpers/statistics/core/parse_statistics_data';
import { estimateChartConsumption } from 'src/helpers/statistics/core/consumptions/consumption_helpers';
import { getAverageStatistic } from '../core/average_statistics_helpers';
import CacheStatisticRequest from './chart_cache_statistics_helpers';
//Types
import type {
	ChartConfig,
	ChartDataInterface,
	ChartStatisticInterface,
	ChartStatisticRangePeriod,
	StatisticsData,
	TimeZoneInterface,
} from 'src/types/Chart';
import type {
	GetStatsPeriodRequest,
	GetStatsResponseData,
	RequestStatisticData,
} from 'src/types/requests/charts/get_stats_period';
import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
import type { Device } from 'src/types/Device';

/**
 * Retrieves statistical data for a given device and chart configuration.
 *
 * Currently used in:
 * - DeviceChartsLayer.svelte
 * - ThumbLayerChart.svelte
 * - pv_setup_helper.ts
 *
 * @param {Device} device - The device for which statistics are being retrieved.
 * @param {ChartControlsDataInterface} chartControlsData - Object containing data to control chart display settings.
 * @param {ChartConfig} chartConfig - Configuration for the chart to be generated.
 * @param {string} reqURL - The URL for the statistics request.
 * @param {number} [netMeteringPeriod=0] - Optional parameter specifying the net metering period.
 * @param {boolean} [showPrevPeriod=false] - Optional flag to indicate if the previous period's data should be shown.
 * @returns {Promise<StatisticsData>} A promise that resolves to the statistics data for the given device and configuration.
 * @throws Will throw an error if the request fails or if there are permissions issues.
 */

export const getStatistics = async (
	device: Device,
	chartControlsData: ChartControlsDataInterface,
	chartConfig: ChartConfig,
	reqURL: string,
	netMeteringPeriod: number = 0,
	showPrevPeriod = false,
	dummyNetMetering: boolean = false,
): Promise<StatisticsData> => {
	const from = chartControlsData.time.from;
	const to = chartControlsData.time.to;
	const oneHour = 60 * 60 * 1000;
	const timeDiff = to.getTime() - from.getTime();
	const dailyRange = timeDiff > oneHour && timeDiff <= oneHour * 24;
	const usesUTC = isBleStatisticsDeviceToUTC(device, chartControlsData.rangeOption.period);
	const reqData: RequestStatisticData = {
		id: device ? real_id(device.id) : undefined,
		channel: (chartConfig.deviceChannel ?? device?.channel) as number,
		date_range: dailyRange ? 'day' : 'custom',
		date_from: dateFormat(from, chartControlsData.format, usesUTC),
		date_to: dailyRange ? undefined : dateFormat(to, chartControlsData.format, usesUTC),
	};
	if (netMeteringPeriod > 0) {
		reqData.period = netMeteringPeriod;
	}

	const res: GetStatsPeriodRequest = await CacheStatisticRequest.getData(
		reqURL,
		reqData,
		device && device.shared ? device?.server : undefined,
	);
	if (res?.isok || res?.history) {
		const resData: GetStatsResponseData = res?.history ? (res as GetStatsResponseData) : res.data;
		return await getStatisticsRequest(
			device,
			chartControlsData,
			chartConfig,
			reqURL,
			reqData,
			resData,
			netMeteringPeriod,
			showPrevPeriod,
			dummyNetMetering,
		);
	} else {
		let error = get(format)('failed_to_get_statistics_data', {
			default: 'Failed to get statistics data',
		});
		if (res?.errors?.device_not_found || res?.errors?.no_permissions) {
			error = res.errors?.device_not_found || res.errors?.no_permissions;
		}
		throw new Error(error);
	}
};

/**
 * Processes and retrieves detailed statistical data for a given device and chart configuration.
 * This function handles net metering, last hour data, and previous period data retrieval as needed.
 *
 * @param {Device} device - The device for which statistics are being processed.
 * @param {ChartControlsDataInterface} chartControlsData - Configuration data controlling the chart's display settings.
 * @param {ChartConfig} chartConfig - Configuration for the chart to be generated.
 * @param {string} reqURL - The URL used to request the statistics data.
 * @param {RequestStatisticData} reqData - The request data parameters used to fetch statistics.
 * @param {GetStatsResponseData} resData - The initial response data to be processed.
 * @param {number} [netMeteringPeriod=0] - Optional parameter specifying the net metering period.
 * @param {boolean} [showPrevPeriod=false] - Optional flag to indicate if the previous period's data should be fetched.
 * @returns {Promise<StatisticsData>} A promise resolving to the processed statistics data.
 */

const getStatisticsRequest = async (
	device: Device,
	chartControlsData: ChartControlsDataInterface,
	chartConfig: ChartConfig,
	reqURL: string,
	reqData: RequestStatisticData,
	resData: GetStatsResponseData,
	netMeteringPeriod: number = 0,
	showPrevPeriod = false,
	dummyNetMetering: boolean = false,
): Promise<StatisticsData> => {
	const netMeteringURL = reqURL.replace('/power-consumption/', '/net-energy/');
	const isConsumption: boolean = chartConfig.name === 'consumption';
	const isVoltage: boolean = chartConfig.name === 'voltage';
	const isMeasurement: boolean =
		isWeatherStation(device?.type) ||
		/(accumulation|measurement|counter|distance)/.test(chartConfig.name);
	const timeZone: TimeZoneInterface = getDeviceTimeZoneDate(device);
	const deviceType: string = device?.type;
	const deviceMode: string = device?.mode;
	const isThreeEMTriphase: boolean = isThreeEM(deviceType) && deviceMode != 'monophase';
	const server: string = device?.shared ? device.server : undefined;
	const rangePeriod: ChartStatisticRangePeriod = chartControlsData.rangeOption.period;
	const period = chartControlsData.rangeOption.period;
	const to: Date = chartControlsData.time.to;

	if (netMeteringPeriod > 0 && isConsumption) {
		if (/^hour$/.test(period)) {
			return {
				device,
				chartData: {
					...chartConfig,
					rangePeriod,
					rangeInterval: resData.history_interval || resData.interval,
					series: [],
				},
				chartStatistic: null,
				resData,
			};
		}
		resData = await getNetMetering(netMeteringURL, reqData, resData, server, isThreeEMTriphase);
	}
	const lastHour = timeZone?.date || new Date();
	lastHour.setMinutes(0, 0, 0);
	const fetchLastHour = !/^hour$/.test(period) && to.getTime() >= lastHour.getTime();
	if (fetchLastHour && ((!netMeteringPeriod && isConsumption) || isMeasurement || isVoltage)) {
		const lastHourReqData: RequestStatisticData = {
			...reqData,
			date_range: 'custom',
			date_from: dateFormat(lastHour, chartControlsData.format, device.gen == 'GBLE'),
			date_to: dateFormat(addDate(lastHour, 'h'), chartControlsData.format, device.gen == 'GBLE'),
		};
		const lastHourRes = await CacheStatisticRequest.getData(reqURL, lastHourReqData, server);
		if (!lastHourRes?.error && lastHourRes?.history) {
			const lastHourResData: GetStatsResponseData = lastHourRes?.history
				? (lastHourRes as GetStatsResponseData)
				: lastHourRes?.data;
			if (isMeasurement) {
				resData = addLastHourMeasurement(
					resData,
					lastHourResData,
					chartControlsData,
					device.gen == 'GBLE',
				);
			} else {
				resData = addLastHourConsumption(
					resData,
					lastHourResData,
					chartControlsData,
					isThreeEMTriphase,
				);
			}
		}
	}

	let prevPeriodData = null;
	if (showPrevPeriod && isConsumption) {
		const from = chartControlsData.time.from;
		const oneHour = 60 * 60 * 1000;
		const oneDay = oneHour * 24;
		const timeDiff = to.getTime() - from.getTime();
		if (period == 'custom') {
			chartControlsData.rangeOption.timeRange = `${Math.ceil(timeDiff / oneDay)}d`;
		}
		const prev = rangeDates(from, chartControlsData.rangeOption, 'subtract', true);
		const dailyRange = timeDiff > oneHour && timeDiff <= oneDay;
		const prevReqData: RequestStatisticData = {
			...reqData,
			date_from: dateFormat(prev.from, chartControlsData.format),
			date_to: dailyRange ? undefined : dateFormat(prev.to, chartControlsData.format),
		};
		const prevRes = await CacheStatisticRequest.getData(reqURL, prevReqData, server);
		if (!prevRes?.error && prevRes?.history) {
			prevPeriodData = prevRes?.history ? prevRes : prevRes.data;
			if (netMeteringPeriod > 0) {
				prevPeriodData = await getNetMetering(
					netMeteringURL,
					prevReqData,
					prevPeriodData,
					server,
					isThreeEMTriphase,
				);
			}
		}
	}

	const chartData: ChartDataInterface = parseStatisticsData(
		resData,
		chartConfig,
		rangePeriod,
		device,
		isThreeEMTriphase ? null : prevPeriodData,
		dummyNetMetering,
		period,
	);
	chartData.deviceMode = deviceMode;

	let chartStatistic: ChartStatisticInterface = null;
	if (isConsumption) {
		const threeEmData =
			isThreeEMTriphase && prevPeriodData
				? {
						channel: 'total',
						prevChartData: parseStatisticsData(
							prevPeriodData,
							chartConfig,
							rangePeriod,
							device,
							null,
							dummyNetMetering,
						),
					}
				: null;
		chartStatistic = estimateChartConsumption(
			chartData,
			deviceType,
			get(user_settings).currency,
			threeEmData,
		);
	} else {
		chartStatistic = getAverageStatistic(chartData);
	}

	return { device, chartData, chartStatistic, resData };
};
